import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusCircle,
  ShoppingBag,
  Truck,
  Wallet,
  Menu,
  X,
  Sparkles,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../redux/store";
import AddMetaCommerce from "./AddMetaCommerce";
import MetaCommerceHostOrders from "./MetaCommerceHostOrders";
import PayoutAgreement from "../PAY/PayoutAgreement";
import CommerceDeliveryZones from "../DELIVERY/CommerceDeliveryZones";
import logo from "../../assets/meta/logo.png";

/* ─── PALETTE (mirrors Glamour) ─── */
const G = {
  black:    "#000000",
  ink:      "#080808",
  dark:     "#0f0f0f",
  panel:    "#141414",
  surface:  "#1a1a1a",
  border:   "#1e1e1e",
  borderHi: "#2a2a2a",
  offwhite: "#ede8df",
  soft:     "#c8c4bc",
  muted:    "#4a4a4a",
  dim:      "#282828",
  gold:     "#a8894a",
  goldLt:   "#bfa06a",
  goldDim:  "#4a3c20",
  goldGlow: "rgba(168,137,74,0.07)",
  goldGlow2:"rgba(168,137,74,0.12)",
};

/* ─── FONTS ─── */
const GoogleFonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
    *, ::before, ::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${G.black}; }
    button, input { font-family: inherit; }
    ::selection { background: ${G.goldGlow2}; color: ${G.offwhite}; }
    ::-webkit-scrollbar { width: 3px; }
    ::-webkit-scrollbar-thumb { background: ${G.border}; }
    .gc-nav-btn {
      width: 100%; display: flex; align-items: center; gap: 10px;
      padding: 10px 14px; border: none; background: transparent;
      cursor: pointer; transition: all 0.18s; font-family: inherit;
      border-left: 2px solid transparent;
    }
    .gc-nav-btn:hover { background: rgba(168,137,74,0.04); }
    .gc-nav-btn.active {
      background: ${G.goldGlow}; border-left-color: ${G.goldDim};
    }
    .gc-icon-btn {
      width: 34px; height: 34px; border: none; background: transparent;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; transition: background 0.15s; border: 1px solid transparent;
    }
    .gc-icon-btn:hover { background: rgba(255,255,255,0.04); border-color: ${G.border}; }
  `}</style>
);

/* ─── NAV CONFIG ─── */
type TabKey = "add" | "orders" | "delivery" | "payout";
const NAV: { id: TabKey; label: string; icon: React.ElementType }[] = [
  { id: "add",      label: "Add Product",    icon: PlusCircle  },
  { id: "orders",   label: "Orders",         icon: ShoppingBag },
  { id: "delivery", label: "Delivery Zones", icon: Truck       },
  { id: "payout",   label: "Payouts",        icon: Wallet      },
];

/* ═══════════════════════════════
   MAIN COMPONENT
═══════════════════════════════ */
const MetaCommerceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ userId?: string }>();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const resolvedUserId = params.userId || authUser?.userId;

  const [activeTab, setActiveTab] = useState<TabKey>("add");
  const [mobile, setMobile]       = useState(window.innerWidth < 1024);
  const [sidebar, setSidebar]     = useState(false);

  /* ── Auth guard ── */
  useEffect(() => {
    if (!authUser)        { navigate("/login"); return; }
    if (!resolvedUserId)  { navigate("/"); }
  }, [authUser, resolvedUserId, navigate]);

  /* ── Responsive ── */
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const navClick = (id: TabKey) => {
    setActiveTab(id);
    if (mobile) setSidebar(false);
  };

  /* ── Page renderer ── */
  const renderMain = () => {
    switch (activeTab) {
      case "add":      return <AddMetaCommerce />;
      case "orders":   return <MetaCommerceHostOrders />;
      case "delivery": return <CommerceDeliveryZones />;
      case "payout":   return <PayoutAgreement />;
      default:         return null;
    }
  };

  /* ══════ RENDER ══════ */
  return (
    <div style={{
      minHeight: "100vh",
      background: G.black,
      fontFamily: "'Montserrat', sans-serif",
      color: G.offwhite,
      display: "flex",
    }}>
      <GoogleFonts />

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobile && sidebar && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSidebar(false)}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 998,
              backdropFilter: "blur(4px)",
            }}
          />
        )}
      </AnimatePresence>

      {/* ══ SIDEBAR ══ */}
      <aside style={{
        width: 240,
        background: G.ink,
        borderRight: `1px solid ${G.border}`,
        display: "flex",
        flexDirection: "column",
        position: mobile ? "fixed" : "sticky",
        top: 0,
        height: "100vh",
        zIndex: 999,
        transform: mobile && !sidebar ? "translateX(-100%)" : "translateX(0)",
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: mobile && sidebar ? "6px 0 32px rgba(0,0,0,0.4)" : "none",
        flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{
          padding: "20px 20px 16px",
          borderBottom: `1px solid ${G.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 28, height: 28,
              background: `linear-gradient(135deg, ${G.goldDim}, ${G.gold})`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Sparkles size={14} color={G.black} strokeWidth={2} />
            </div>
            <div>
              <img src={logo} alt="Logo" style={{ height: 14, display: "block", filter: "brightness(0) invert(0.85) sepia(0.2)" }} />
              <div style={{
                fontSize: 8, color: G.muted,
                letterSpacing: 1.5, textTransform: "uppercase" as const,
                marginTop: 2,
              }}>
                Host Portal
              </div>
            </div>
          </div>
          {mobile && (
            <button className="gc-icon-btn" onClick={() => setSidebar(false)}>
              <X size={16} color={G.muted} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, overflowY: "auto", padding: "10px 0" }}>
          {NAV.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                className={`gc-nav-btn${active ? " active" : ""}`}
                onClick={() => navClick(id)}
              >
                <Icon
                  size={15}
                  color={active ? G.gold : G.muted}
                  strokeWidth={1.5}
                />
                <span style={{
                  fontSize: 11,
                  fontWeight: active ? 600 : 400,
                  color: active ? G.offwhite : G.muted,
                  flex: 1,
                  textAlign: "left" as const,
                }}>
                  {label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Sidebar footer — user email */}
        <div style={{ padding: "14px 16px", borderTop: `1px solid ${G.border}` }}>
          <div style={{ fontSize: 10, color: G.muted, letterSpacing: 0.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
            {authUser?.email || ""}
          </div>
        </div>
      </aside>

      {/* ══ MAIN ══ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

        {/* TOPBAR */}
        <header style={{
          height: 54,
          background: "rgba(8,8,8,0.97)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${G.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 20px",
          position: "sticky", top: 0, zIndex: 100,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {mobile && (
              <button className="gc-icon-btn" onClick={() => setSidebar(true)}>
                <Menu size={16} color={G.muted} strokeWidth={1.5} />
              </button>
            )}
            <h1 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 16, fontWeight: 500,
              color: G.offwhite, letterSpacing: 2,
              textTransform: "uppercase" as const,
            }}>
              {NAV.find((n) => n.id === activeTab)?.label ?? "Dashboard"}
            </h1>
          </div>

          {/* Avatar initial */}
          <div style={{
            width: 28, height: 28,
            background: `linear-gradient(135deg, ${G.goldDim}, ${G.gold})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: `1px solid ${G.goldDim}`,
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: G.black }}>
              {authUser?.email?.charAt(0)?.toUpperCase() ?? "G"}
            </span>
          </div>
        </header>

        {/* PAGE CONTENT */}
        <main style={{
          flex: 1,
          padding: mobile ? "20px 14px" : "28px 28px",
          overflowY: "auto",
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22, ease: [0.2, 0, 0.1, 1] }}
            >
              {renderMain()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default MetaCommerceDashboard;